import moment from 'moment';

import { TitleCasePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  inject,
  viewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

import {
  SimpleTableColumnModel,
  SimpleTableConfig,
  createSimpleTableConfig,
} from '../../interfaces';
import {
  ActivityDataSource,
  ActivityLogResponse,
} from '../../interfaces/activity-log.interface';
import { TimeAgoPipe } from '../../pipes';

const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

const escapeRegex = (text: string): string => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
};

@Component({
  standalone: false,
  selector: 'phoenix-activity-log',
  templateUrl: './activity-log.component.html',
})
export class ActivityLogComponent implements OnChanges {
  readonly titleCase = inject(TitleCasePipe);
  readonly timeAgoPipe = inject(TimeAgoPipe);

  @Input() tableId = 'Activity Log';
  @Input() activityDataSourceLength = 0;
  @Input() activityPageSize = 10;
  @Input() activityPageToken = 0;
  @Input() activityFilterText = '';
  @Input() isLoadingActivityLogTable = false;
  @Input() searchBoxPlaceholder = 'Search by email';
  @Input() dataSource!: ActivityLogResponse;

  @Output() searchboxChange = new EventEmitter<any>();

  activityLog!: MatTableDataSource<ActivityDataSource>;
  activityLogExpandable = viewChild<TemplateRef<unknown>>(
    'activityLogExpandable'
  );

  activityColumnDefList: SimpleTableColumnModel[] = [
    {
      key: 'event',
      title: 'Event',
      value: (value: any) => {
        return `${this.formatEvent(value.event)} ${this.timeAgoPipe.transform(value.date as unknown as string)}`;
      },
      tooltip: (value: any) =>
        moment.utc(value.date).format('MMMM D, YYYY, h:mm A'),
      class: 'w-[65%]',
    },
    {
      key: 'user',
      title: 'User',
      value: (value: any) => {
        return value.user ?? value.service ?? value.script;
      },
    },
  ];
  activityDisplayedColumns = ['user', 'event'];
  activityTableConfig: SimpleTableConfig = {
    ...createSimpleTableConfig(),
    columnDefList: this.activityColumnDefList,
    displayedColumns: this.activityDisplayedColumns,
    serverSidePagination: true,
    isSortable: false,
    hasSearchbox: true,
    isSelectable: false,
    hasPaginator: true,
    isCollapsible: true,
    templateExpandible: this.activityLogExpandable,
    id: this.tableId,
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] || changes['activityFilterText']) {
      this.activityLogFormat();
    }
  }

  // Only title-case the leading word, so an appended email/id keeps its casing.
  private formatEvent(notes?: string): string {
    const value = notes ?? '';
    const [firstWord, ...rest] = value.split(' ');
    const label = this.titleCase.transform(firstWord);

    return rest.length ? `${label} ${rest.join(' ')}` : label;
  }

  private highlightSearchTerm(value?: string): string | undefined {
    if (!value) return value;

    const safeValue = escapeHtml(value);
    const search = this.activityFilterText.trim();

    if (!search) return safeValue;

    const searchRegex = new RegExp(`(${escapeRegex(search)})`, 'ig');
    return safeValue.replace(searchRegex, '<em class="text-highlight">$&</em>');
  }

  activityLogFormat() {
    this.activityLog = new MatTableDataSource<ActivityDataSource>([]);
    this.activityDataSourceLength = 0;
    if (this.dataSource?.edges?.length) {
      const formattedActivityLog: ActivityDataSource[] =
        this.dataSource.edges.map((edge: any) => {
          return {
            date: edge.node.history.time,
            event: edge.node.history.notes,
            user: this.highlightSearchTerm(edge.node.user),
            notes: edge.node.history.notes,
            service: this.highlightSearchTerm(edge.node.service),
            script: this.highlightSearchTerm(edge.node.script),
            history:
              edge.node.history?.changes?.sort((a: any, b: any) => {
                if (a.path === '/updated_at' && b.path !== '/updated_at')
                  return 1;
                if (a.path !== '/updated_at' && b.path === '/updated_at')
                  return -1;
                return 0;
              }) ?? [],
          };
        });
      this.activityLog = new MatTableDataSource(
        formattedActivityLog.length ? formattedActivityLog : []
      );
      this.activityDataSourceLength = this.dataSource.total;
    }
  }
}
